import _ from "lodash";
import LocalStorageService from "service/localStorageService";
import axiosInstance from "./axiosInstance";
import { getRandomString } from "utils/functions";

// Check if the current access token is valid, if not it would be re-generated automatically
export const websocketCheck = async () => {
    try {
        await axiosInstance.get("/ws/check");
    } catch (error) {
        // Must catch the error because this API will throw 404 error even if the token is valid
    }
};

const APP_DASHBOARD_API = process.env.REACT_APP_DASHBOARD_API;
// Change the protocol to wss
const WS_DASHBOARD_API = APP_DASHBOARD_API?.replace("https", "wss");

type ResponseData = {
    isError: boolean;
    messageId: string;
    data: any;
};

class WebsocketQuery {
    private requests: Record<
        string,
        {
            callback?: (response: any) => void;
        }
    > = {};
    private instance: WebSocket | null = null;
    public ready = false;
    private connectionStatus: "connected" | "disconnected" | "reconnecting" =
        "disconnected";
    private connectionPromise: Promise<void> | null = null;
    private orgId: string | null = null;

    public onMessage: (data: any) => void = () => {};
    public onSuccess: () => void = () => {};
    public onClose: (event: CloseEvent) => void = () => {};

    private clearConnection() {
        if (this.instance) {
            WebsocketQuery.cleanUpWS(this.instance);
            this.instance = null;
        }
        this.ready = false;
        this.connectionStatus = "disconnected";
        this.connectionPromise = null;
        this.orgId = null;
        this.requests = {};
    }

    private async generateAccessToken() {
        try {
            await websocketCheck();
        } catch (error) {
            // Intentionally left empty
        }
    }

    static cleanUpWS(wsInstance: WebSocket | null) {
        if (wsInstance) {
            wsInstance.close();
            wsInstance.onopen = null;
            wsInstance.onmessage = null;
            wsInstance.onerror = null;
            wsInstance.onclose = null;
        }
    }

    private async connectRequest(orgId: string): Promise<WebSocket> {
        const tokens = LocalStorageService.getItem("tokens");
        const accessToken = tokens?.AccessToken;
        let url = `${WS_DASHBOARD_API}/ws/query/${orgId}?token=${encodeURIComponent(
            accessToken
        )}`;

        const connectionPromise = new Promise<WebSocket>((resolve, reject) => {
            WebsocketQuery.cleanUpWS(this.instance);

            const instance = new WebSocket(url);

            instance.onclose = (event: CloseEvent) => {
                if (typeof this.onClose === "function") {
                    this.onClose(event);
                }

                reject();
            };

            instance.onmessage = (event: MessageEvent) => {
                if (typeof this.onMessage === "function") {
                    this.onMessage(event);
                }
            };

            instance.onopen = () => {
                if (typeof this.onSuccess === "function") {
                    this.onSuccess();
                }

                resolve(instance);
            };

            instance.onerror = (error: Event) => {};

            this.instance = instance;
        });

        return connectionPromise;
    }

    static formatResponse(response: string): any {
        let responseData: any;
        try {
            responseData = JSON.parse(response);
        } catch (error) {
            responseData = "";
        }

        const isError = !!responseData?.error;
        let messageId = "";

        if (responseData?.id) {
            messageId = responseData.id;
            delete responseData.id;
        }

        const result = {
            isError,
            messageId,
            data: responseData,
        };

        return result;
    }

    /**
     * Check if the WebSocket connection is ready for a specific organization
     * @param orgId The organization ID to check the connection status for.
     *              Must provide this as each organization may have its own connection.
     */
    isReady(orgId: string): boolean {
        if (!orgId || this.orgId !== orgId) {
            return false;
        }

        return this.connectionStatus === "connected" && this.instance !== null;
    }

    // Consider to init the connection with timeout configuration
    async initConnection(orgId: string) {
        // Reset the connection if the orgId has changed
        if (this.orgId !== orgId) {
            this.clearConnection();
        }

        // WebSocket connection is already initialized or reconnecting.
        if (this.ready || this.connectionStatus === "reconnecting") {
            return this.connectionPromise;
        }

        this.orgId = orgId;
        this.connectionStatus = "reconnecting";

        this.connectionPromise = new Promise(async (resolve, reject) => {
            try {
                await this.generateAccessToken();
                await this.connectRequest(orgId);

                this.ready = true;
                this.connectionStatus = "connected";

                this.onClose = (event: CloseEvent) => {
                    this.clearConnection();
                };

                this.onMessage = (event: MessageEvent) => {
                    const response = WebsocketQuery.formatResponse(event.data);
                    const { messageId } = response;

                    // Check and call the callback function if the messageId exists in request list
                    const callbackFn = _.get(
                        this.requests,
                        `[${messageId}].callback`
                    ) as Function;

                    if (typeof callbackFn === "function") {
                        callbackFn(response);
                        delete this.requests[messageId];
                    }
                };

                resolve();
            } catch (error) {
                this.clearConnection();
                reject(error);
            }
        });

        return this.connectionPromise;
    }

    fetch(params: { query: string; time: number }): Promise<any> {
        const messageId = getRandomString();

        return new Promise((resolve, reject) => {
            // Can be considered to improve to wait for the connection to be finished if any
            if (!this.ready) {
                // This error message is not expected to be shown to the user.
                // The connection should be established before calling fetch.
                reject(new Error("Connection is not ready."));
                return;
            }

            this.onResponse(messageId, (response) => {
                if (response.isError) {
                    reject(response.data);
                    return;
                }

                resolve(response.data);
            });

            this.instance?.send(JSON.stringify({ id: messageId, ...params }));
        });
    }

    onResponse(messageId: string, callback: (data: ResponseData) => void) {
        if (!this.requests[messageId]) {
            this.requests[messageId] = {};
        }

        if (typeof callback === "function") {
            this.requests[messageId].callback = callback;
        }
    }
}

export default WebsocketQuery;
